import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PROJECT_DETAILS } from "@/data/projectDetails";
import { SITE } from "@/data/content";
import Footer from "@/components/portfolio/Footer";
import { scrollToTop } from "@/lib/scroll";

const ease = [0.16, 1, 0.3, 1];

export default function ProjectDetail() {
  const { slug } = useParams();
  const data = PROJECT_DETAILS[slug];

  useEffect(() => {
    scrollToTop(true);
  }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <main data-testid={`project-detail-${slug}`} className="bg-white text-black min-h-screen">
      {/* Top bar */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/10">
        <div className="px-6 md:px-12 lg:px-16 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl md:text-2xl">
            <span className="italic font-light">Isabella</span> Rubino
          </Link>
          <Link
            data-testid="back-to-work"
            to="/#work"
            className="text-[11px] uppercase tracking-[0.22em] font-medium link-underline"
          >
            ← All Work
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-16 pt-32 md:pt-44 pb-16 md:pb-24">
        <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-6">
          {data.category}
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          className="font-serif font-light tracking-tighter leading-[1.04] text-[clamp(2.25rem,6vw,5.25rem)] max-w-[20ch] pt-[0.08em] pb-[0.06em]"
        >
          {data.title}
        </motion.h1>
        <p className="mt-10 font-serif text-xl md:text-2xl leading-snug text-neutral-700 max-w-[58ch]">
          {data.intro}
        </p>
      </section>

      {/* Sections */}
      <div className="px-6 md:px-12 lg:px-16 pb-24 md:pb-40 space-y-24 md:space-y-32">
        {data.sections.map((s, i) => (
          <motion.section
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="grid grid-cols-12 gap-6 md:gap-12"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-3">
                ({String(i + 1).padStart(2, "0")})
              </p>
              <h2 className="font-serif font-light tracking-tight text-3xl md:text-4xl leading-[1.1]">
                {s.heading}
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-sans text-[15px] md:text-base leading-relaxed text-neutral-700 max-w-[60ch]">
                {s.copy}
              </p>
              {s.links && (
                <ul className="mt-8 space-y-3">
                  {s.links.map((l) => (
                    <li key={l.href}>
                      <a
                        data-testid={`detail-link-${slug}`}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-serif italic text-lg md:text-xl link-underline"
                      >
                        {l.label} →
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {s.images && s.images.length > 0 && (
                <div className="mt-10 grid grid-cols-2 gap-4 md:gap-6">
                  {s.images.map((src) => (
                    <div key={src} className="overflow-hidden aspect-[4/5] bg-neutral-100 col-span-2 md:col-span-1">
                      <img
                        src={src}
                        alt={s.heading}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        ))}
      </div>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 border-t border-black/10 text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-6">
          Interested in working together?
        </p>
        <a
          href={`mailto:${SITE.email}`}
          className="font-serif italic text-3xl md:text-5xl link-underline"
        >
          {SITE.email}
        </a>
      </section>

      <Footer />
    </main>
  );
}
